import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { openCalendly } from "../../lib/calendly";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactFooter() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Kérlek, töltsd ki az összes mezőt.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Köszönöm, megérkezett az üzeneted.");
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail || "Valami nem sikerült. Próbáld újra később.";
      toast.error(typeof msg === "string" ? msg : "Hiba történt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kapcsolat"
      data-testid="contact-section"
      className="relative pt-6 md:pt-8 pb-10 px-6 md:px-10 bg-tinted"
    >
      {/* PRIMARY CTA — Calendly time booking (visually strongest) */}
      <div
        data-testid="primary-cta-block"
        className="max-w-2xl mx-auto text-center"
      >
        <p className="text-[12px] tracking-[0.35em] uppercase text-gold mb-5 font-body">
          — Időpontfoglalás
        </p>
        <h2
          data-testid="contact-title"
          className="font-serif-display text-[36px] sm:text-[44px] md:text-[52px] leading-[1.1] text-graphite"
        >
          Induljunk el{" "}
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            együtt
          </em>
          .
        </h2>
        <p className="mt-6 text-graphite font-body font-light text-[16px] md:text-[17px] leading-[1.85] max-w-md mx-auto">
          Foglalj időpontot egyéni konzultációra — egyszerűen, néhány kattintással, a
          számodra megfelelő napon.
        </p>

        {/* Transition note — Forrás-Kód® díjszabás vonatkozik rá */}
        <p
          data-testid="forras-kod-pricing-note"
          className="mt-8 md:mt-10 font-serif-display italic text-graphite text-[17px] md:text-[19px] leading-[1.65] max-w-xl mx-auto"
        >
          A fent említett folyamat is a Forrás-Kód<sup className="text-gold">®</sup> kísérés
          keretén belül zajlik.{" "}
          <span className="text-gold">Várlak szeretettel.</span>
        </p>

        {/* Previous "35 000 Ft" — now struck-through to signal the shift */}
        <div
          data-testid="pricing-line"
          className="mt-8 md:mt-10 flex items-center justify-center gap-5 md:gap-6 max-w-sm mx-auto"
        >
          <span className="flex-1 h-px bg-hairline" />
          <p className="font-serif-display text-muted-warm text-[20px] md:text-[22px] leading-none whitespace-nowrap tracking-tight">
            <span
              style={{
                textDecoration: "line-through",
                textDecorationColor: "var(--muted-warm, #9a9080)",
                textDecorationThickness: "1px",
              }}
            >
              35&thinsp;000&nbsp;Ft
            </span>
            <span className="italic font-light text-[13px] md:text-[14px] tracking-normal">
              {" "}/ alkalom
            </span>
          </p>
          <span className="flex-1 h-px bg-hairline" />
        </div>

        {/* NEW — Forrás-Kód® díjszabásos táblázat (ugyanaz, mint a Forrás-Kód Kísérés aloldalon) */}
        <ForrasKodPricingTable />

        <div className="mt-10 md:mt-12 flex justify-center">
          <button
            type="button"
            onClick={openCalendly}
            data-testid="calendly-cta-button"
            className="cta-graphite inline-flex flex-col items-center gap-1.5 px-12 py-6 font-body font-medium"
          >
            <span className="text-[11px] tracking-[0.35em] uppercase opacity-80">
              Induljunk el együtt
            </span>
            <span className="text-[16px] tracking-[0.2em] uppercase flex items-center gap-3">
              Időpont foglalása
              <span aria-hidden="true">→</span>
            </span>
          </button>
        </div>

        <p className="mt-6 text-[12px] tracking-[0.25em] uppercase text-muted-warm font-body flex items-center justify-center gap-3">
          <span className="gold-dot" />
          <span>Egyéni konzultáció</span>
        </p>
      </div>

      {/* Soft divider between the two CTAs */}
      <div className="max-w-md mx-auto mt-16 md:mt-20 mb-12 md:mb-14 flex items-center gap-5">
        <span className="flex-1 h-px bg-hairline" />
        <span className="font-serif-display italic text-graphite-soft text-[15px] md:text-[16px]">
          vagy
        </span>
        <span className="flex-1 h-px bg-hairline" />
      </div>

      {/* SECONDARY CTA — soft, contact form for those not ready yet */}
      <div
        data-testid="secondary-cta-block"
        className="max-w-2xl mx-auto text-center"
      >
        <p className="text-[12px] tracking-[0.3em] uppercase text-muted-warm mb-4 font-body">
          — Van még kérdésed?
        </p>
        <h3 className="font-serif-display text-[26px] sm:text-[30px] md:text-[34px] leading-[1.25] text-graphite">
          Írj nekem <em className="text-gold" style={{ fontStyle: "italic" }}>néhány sort</em>.
        </h3>
        <p className="mt-5 text-graphite font-body font-light text-[15.5px] md:text-[16.5px] leading-[1.8] max-w-md mx-auto">
          Ha még nem állsz készen időpontot foglalni, vagy szeretnél előtte kérdezni
          valamit, írj bátran.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        data-testid="contact-form"
        noValidate
        className="max-w-2xl mx-auto mt-10 md:mt-12"
      >
        {sent ? (
          <div
            data-testid="contact-success"
            className="text-center py-14 border border-hairline bg-white"
          >
            <p className="font-serif-display italic text-2xl text-graphite mb-3">
              Köszönöm.
            </p>
            <p className="text-graphite-soft font-body font-light text-[15px]">
              Megérkezett az üzeneted. Hamarosan jelentkezem.
            </p>
            <button
              type="button"
              data-testid="contact-send-another"
              onClick={() => setSent(false)}
              className="mt-8 text-[12px] tracking-[0.3em] uppercase text-gold font-body underline-offset-4 hover:underline"
            >
              Új üzenet küldése
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            <div>
              <label
                htmlFor="name"
                className="block text-[11px] tracking-[0.3em] uppercase text-muted-warm mb-2 font-body"
              >
                Neved
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                data-testid="contact-input-name"
                placeholder="Hogy szólíthatlak?"
                className="input-underline"
                required
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-[11px] tracking-[0.3em] uppercase text-muted-warm mb-2 font-body"
              >
                E-mail
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                data-testid="contact-input-email"
                placeholder="pelda@email.hu"
                className="input-underline"
                required
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-[11px] tracking-[0.3em] uppercase text-muted-warm mb-2 font-body"
              >
                Üzeneted
              </label>
              <textarea
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                data-testid="contact-input-message"
                placeholder="Írd le, mi hozott ide…"
                rows={5}
                className="input-underline resize-none"
                required
              />
            </div>

            <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="text-[12px] text-muted-warm font-body font-light max-w-xs leading-relaxed">
                Az üzeneted bizalmasan kezelem. Nem osztom meg, nem továbbítom.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="cta-gold-outline inline-flex items-center gap-3 px-8 py-3.5 text-[12.5px] uppercase font-body font-medium disabled:opacity-60"
              >
                {loading ? "Küldés…" : "Üzenet küldése"}
                {!loading && <span aria-hidden="true">→</span>}
              </button>
            </div>
          </div>
        )}
      </form>

      {/* Footer */}
      <footer
        data-testid="footer"
        className="max-w-2xl mx-auto mt-10 md:mt-12 pt-8 border-t border-hairline"
      >
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="font-serif-display text-xl text-graphite">
            Szász-Tóth <span className="text-gold italic">Enikő</span>
          </p>
          <div className="flex items-center gap-6 text-[12px] tracking-[0.25em] uppercase text-muted-warm font-body">
            <span className="gold-dot" />
            <span>© {new Date().getFullYear()} — Minden jog fenntartva</span>
          </div>
        </div>
        <p className="mt-6 text-[12px] text-muted-warm font-body font-light italic max-w-md">
          „Minden kapcsolat hordoz valamilyen ajándékot."
        </p>
      </footer>
    </section>
  );
}

/* ===================================================================== */
/* Forrás-Kód® díjszabásos táblázat — ugyanaz, mint a FKK aloldalon       */
/* ===================================================================== */
function ForrasKodPricingTable() {
  return (
    <div
      data-testid="forras-kod-pricing-table"
      className="mt-8 md:mt-10 max-w-xl mx-auto border border-gold bg-warm"
      style={{ boxShadow: "0 10px 40px -24px rgba(160, 122, 40, 0.22)" }}
    >
      {/* Table header */}
      <div className="px-6 md:px-8 pt-7 pb-5 text-center border-b border-hairline">
        <p className="font-body text-[10.5px] md:text-[11px] tracking-[0.35em] uppercase text-gold mb-2">
          — Forrás-Kód<sup>®</sup> kísérés
        </p>
        <p className="font-serif-display italic text-graphite-soft text-[13px] md:text-[14px] leading-[1.6]">
          Kedvezőbb díj a közös folyamat keretén belül
        </p>
      </div>

      {/* Row 1 — Egyéni */}
      <PricingRow
        name="Egyéni kísérés"
        duration="45 perc"
        price="34 000 Ft"
        suffix="/ alkalom"
        note="A második alkalomtól 30 000 Ft / alkalom, ha további 6 alkalmat vásárolsz."
      />

      {/* Row 2 — Csoportos */}
      <PricingRow
        name="Csoportos kísérés"
        duration="120 perc"
        price="12 000 Ft"
        suffix="/ fő / alkalom"
      />
    </div>
  );
}

function PricingRow({ name, duration, price, suffix, note }) {
  return (
    <div className="px-6 md:px-8 py-6 md:py-7 border-b last:border-b-0 border-hairline">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 sm:gap-6 items-center text-center sm:text-left">
        <p className="font-serif-display text-graphite text-[18px] md:text-[20px] leading-[1.3]">
          {name}
        </p>
        <p className="font-body text-[11px] md:text-[12px] tracking-[0.28em] uppercase text-muted-warm sm:text-right whitespace-nowrap">
          {duration}
        </p>
        <p className="font-serif-display text-gold text-[20px] md:text-[22px] leading-none whitespace-nowrap sm:text-right tracking-tight">
          {price}
          <span className="text-muted-warm italic font-light text-[12px] md:text-[13px] tracking-normal">
            {" "}
            {suffix}
          </span>
        </p>
      </div>
      {note && (
        <p className="mt-3 text-[12.5px] md:text-[13px] text-muted-warm font-body font-light italic leading-[1.7] text-center sm:text-left">
          {note}
        </p>
      )}
    </div>
  );
}
